<template>
  <div class="chatbot-panel d-flex flex-column fill-height bg-primary-700">
    <div
      class="chatbot-header d-flex align-center justify-space-between pa-4 bg-primary-600"
    >
      <div class="d-flex align-center">
        <v-icon color="tertiary-900" class="mr-2">mdi-robot-outline</v-icon>
        <span class="text-subtitle-1 font-weight-bold text-white">{{
          t("chatbot.title")
        }}</span>
      </div>
      <div>
        <v-tooltip location="bottom" :text="t('chatbot.reset')">
          <template v-slot:activator="{ props: tooltipProps }">
            <v-btn
              v-bind="tooltipProps"
              icon
              size="small"
              variant="text"
              color="primary-300"
              :disabled="store.sending"
              @click="store.resetConversation()"
            >
              <v-icon size="20">mdi-refresh</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
        <v-btn
          icon
          size="small"
          variant="text"
          color="primary-300"
          @click="store.closePanel()"
        >
          <v-icon size="20">mdi-close</v-icon>
        </v-btn>
      </div>
    </div>

    <div
      ref="historyRef"
      class="chatbot-history flex-grow-1 overflow-y-auto pa-4"
    >
      <div
        v-if="store.messages.length === 0"
        class="chatbot-empty d-flex flex-column align-center justify-center h-100 text-center px-4"
      >
        <v-icon size="52" color="tertiary-900" class="mb-3"
          >mdi-robot-outline</v-icon
        >
        <p class="text-subtitle-2 font-weight-bold text-white mb-1">
          {{ t("chatbot.title") }}
        </p>
        <p class="text-body-2 text-primary-300 mb-0">
          {{ t("chatbot.empty-state") }}
        </p>
      </div>

      <ChatbotMessage
        v-for="message in store.messages"
        :key="message.id"
        :message="message"
        class="mb-3"
        @thumbsUp="onThumbsUp"
        @thumbsDown="onThumbsDown"
        @openLightbox="lightboxSrc = $event"
      />

      <div v-if="store.sending" class="chatbot-typing d-flex align-center">
        <v-progress-circular
          indeterminate
          size="16"
          width="2"
          color="tertiary-900"
          class="mr-2"
        />
        <span class="text-caption text-primary-300">{{
          t("chatbot.typing")
        }}</span>
      </div>
    </div>

    <div class="chatbot-footer pa-4 bg-primary-600">
      <div
        v-if="showMentionHint"
        class="chatbot-mention-hint d-flex align-center rounded-lg pa-2 mb-2"
      >
        <v-icon size="16" color="tertiary-900" class="mr-2">mdi-at</v-icon>
        <span class="text-caption text-primary-100 flex-grow-1">
          {{ t("chatbot.mention-hint") }}
        </span>
        <v-btn
          icon
          size="x-small"
          variant="text"
          color="primary-300"
          class="ml-1"
          @click="dismissMentionHint"
        >
          <v-icon size="14">mdi-close</v-icon>
        </v-btn>
      </div>
      <div class="chatbot-field-wrapper">
        <ChatbotMentionDropdown
          v-if="mention.showDropdown.value"
          :entities="mention.filteredEntities.value"
          :active-index="mention.activeIndex.value"
          class="chatbot-mention-dropdown"
          @select="mention.selectEntity"
        />

        <label class="chatbot-field rounded-lg">
        <v-textarea
          ref="vtextareaRef"
          v-model="draft"
          :placeholder="t('chatbot.placeholder')"
          variant="plain"
          density="compact"
          rows="1"
          auto-grow
          max-rows="4"
          hide-details
          class="chatbot-textarea"
          :disabled="store.sending"
          @keydown="handleKeydown"
          @click="mention.onCursorMove()"
          @keyup="mention.onCursorMove($event)"
        />
        <v-btn
          class="chatbot-send-btn"
          icon
          size="34"
          color="tertiary-900"
          :disabled="!draft.trim() || store.sending"
          elevation="0"
          @click="send"
        >
          <v-icon size="20">mdi-arrow-up</v-icon>
        </v-btn>
        </label>
      </div>
      <p class="text-caption text-primary-300 mt-2 mb-0 text-center">
        {{ t("chatbot.disclaimer") }}
      </p>
    </div>

    <ChatbotFeedbackModal
      v-model="feedbackModalOpen"
      @submit="(comment) => store.submitFeedback(feedbackTargetId!, 0, comment)"
    />
    <ChatbotLightbox v-model="lightboxSrc" />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";

import ChatbotFeedbackModal from "@/components/molecules/chatbot/ChatbotFeedbackModal.vue";
import ChatbotLightbox from "@/components/molecules/chatbot/ChatbotLightbox.vue";
import ChatbotMentionDropdown from "@/components/molecules/chatbot/ChatbotMentionDropdown.vue";
import ChatbotMessage from "@/components/molecules/chatbot/ChatbotMessage.vue";
import { useMention } from "@/composables/useMention";
import { useStorageService } from "@/services/storage-service";
import { useChatbotStore } from "@/store/chatbot";

const { t } = useI18n();
const store = useChatbotStore();
const storageService = useStorageService();
const { entities } = storeToRefs(store);
const draft = ref("");
const historyRef = ref<HTMLElement>();
const vtextareaRef = ref();

const lightboxSrc = ref("");
const feedbackTargetId = ref<string | null>(null);
const feedbackModalOpen = computed({
  get: () => feedbackTargetId.value !== null,
  set: (val) => {
    if (!val) feedbackTargetId.value = null;
  },
});

function getTextarea(): HTMLTextAreaElement | null {
  return vtextareaRef.value?.$el?.querySelector("textarea") ?? null;
}

const mention = useMention(draft, entities, getTextarea);

const showMentionHint = ref(!storageService.getChatbotMentionHintDismissed());

function dismissMentionHint() {
  showMentionHint.value = false;
  storageService.setChatbotMentionHintDismissed();
}

async function send() {
  if (!draft.value.trim() || store.sending) return;
  const message = draft.value;
  draft.value = "";
  await store.sendMessage(message);
}

function handleKeydown(event: KeyboardEvent) {
  if (mention.onKeydown(event)) return;
  if (event.key === "Enter" && !event.shiftKey) {
    event.preventDefault();
    send();
  }
}

function onThumbsUp(messageId: string) {
  store.submitFeedback(messageId, 1);
}

function onThumbsDown(messageId: string) {
  feedbackTargetId.value = messageId;
}

watch(
  () => store.messages.length,
  () => {
    nextTick(() => {
      historyRef.value?.scrollTo({
        top: historyRef.value.scrollHeight,
        behavior: "smooth",
      });
    });
  },
  { immediate: true },
);
</script>

<style scoped>
.chatbot-panel {
  height: 100%;
}

.chatbot-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
}

.chatbot-footer {
  flex-shrink: 0;
}

.chatbot-field-wrapper {
  position: relative;
}

.chatbot-mention-hint {
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.05);
}

.chatbot-mention-dropdown {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 10;
}

.chatbot-field {
  display: block;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.05);
  padding: 0 12px 42px 12px;
  cursor: text;
  transition: border-color 0.15s ease;
}

.chatbot-field:focus-within {
  border-color: rgba(255, 255, 255, 0.4);
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.15);
}

.chatbot-send-btn {
  position: absolute;
  bottom: 8px;
  right: 8px;
}

.chatbot-textarea :deep(.v-field) {
  --v-field-padding-start: 0px;
  --v-field-padding-end: 0px;
}

.chatbot-textarea :deep(textarea) {
  color: white;
}

.chatbot-textarea :deep(textarea::placeholder) {
  color: rgba(255, 255, 255, 0.5);
  opacity: 1;
}

</style>
